import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import PublicIcon from "@mui/icons-material/Public";
import api from "../../../services/api/axios";
import BrandSpinner from "../../../shared/components/BrandSpinner";
import { useAuth } from "../../../providers/AuthProvider";
import { formatDurationSeconds } from "../../../utils/formatters";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

function clean(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDomain(value) {
  const input = clean(value);
  if (!input) return "";

  try {
    const url = new URL(
      /^https?:\/\//i.test(input) ? input : `https://${input}`,
    );
    return url.hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return input
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split("/")[0]
      .toLowerCase();
  }
}

function groupMatchesCompany(group, company) {
  const row = group?.representative || {};

  const companyDynamicsId = clean(company?.dynamics_account_id).toLowerCase();
  const visitDynamicsId = clean(row?.visitor_account_id).toLowerCase();
  if (companyDynamicsId && visitDynamicsId && companyDynamicsId === visitDynamicsId) {
    return 4;
  }

  const companyDomain =
    normalizeDomain(company?.domain) || normalizeDomain(company?.website);
  const visitDomain =
    normalizeDomain(row?.visitor_domain) ||
    normalizeDomain(row?.visitor_website);

  if (companyDomain && visitDomain && companyDomain === visitDomain) {
    return 3;
  }

  const companyName = clean(company?.name).toLowerCase();
  const visitName =
    clean(row?.visitor_company_name || row?.visitor_account_name).toLowerCase();
  const companyState = clean(company?.state).toLowerCase();
  const visitState = clean(row?.visitor_state).toLowerCase();

  if (companyName && visitName && companyName === visitName) {
    if (companyState && visitState && companyState === visitState) {
      return 2;
    }
    return 1;
  }

  return 0;
}

function summarizePages(group) {
  const pages = Array.isArray(group?.pages) ? group.pages : [];
  const pageMap = new Map();

  for (const page of pages) {
    const url = clean(page?.url);
    if (!url) continue;

    const current = pageMap.get(url) || {
      url,
      visits: 0,
      totalDurationSeconds: 0,
    };

    current.visits += 1;
    current.totalDurationSeconds += Math.max(
      Number(page?.duration_seconds) || 0,
      0,
    );

    pageMap.set(url, current);
  }

  return Array.from(pageMap.values()).sort((a, b) => {
    if (b.visits !== a.visits) return b.visits - a.visits;
    return b.totalDurationSeconds - a.totalDurationSeconds;
  });
}

export default function ProspectingCompanyWebpagesVisitedTab({ company }) {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const organizationGuid = clean(user?.organization_guid);
    if (!organizationGuid) {
      setGroups([]);
      setLoading(false);
      setError("This organization does not have website tracking configured.");
      return undefined;
    }

    const run = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(
          `/website-visits/${organizationGuid}/grouped`,
          { timeout: 300000 },
        );

        if (cancelled) return;
        setGroups(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (cancelled) return;
        setGroups([]);
        setError(
          err?.response?.data?.detail ||
            "Failed to load webpage visits for this company.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [user?.organization_guid]);

  const matchedGroup = useMemo(() => {
    let bestGroup = null;
    let bestScore = 0;

    for (const group of groups) {
      const score = groupMatchesCompany(group, company);
      if (score > bestScore) {
        bestGroup = group;
        bestScore = score;
      }
    }

    return bestGroup;
  }, [company, groups]);

  const pageSummaries = useMemo(
    () => summarizePages(matchedGroup),
    [matchedGroup],
  );

  if (loading) {
    return (
      <BrandSpinner compact py={6} />
    );
  }

  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }

  if (!matchedGroup) {
    return (
      <Alert severity="info">
        No tracked webpage visits were matched to this prospecting company.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Chip
          label={`${matchedGroup.total_visits || 0} Total Visits`}
          sx={{ backgroundColor: BRAND_BLUE, color: "white", fontWeight: 700 }}
        />
        <Chip
          label={`${pageSummaries.length} Unique Pages`}
          sx={{
            backgroundColor: `${BRAND_GREEN}22`,
            color: BRAND_BLUE,
            fontWeight: 700,
            border: `1px solid ${BRAND_GREEN}`,
          }}
        />
        {matchedGroup?.representative?.last_seen && (
          <Chip
            label={`Last Seen ${dayjs(matchedGroup.representative.last_seen).format("YYYY-MM-DD")}`}
            variant="outlined"
            sx={{ color: BRAND_BLUE, borderColor: `${BRAND_BLUE}66`, fontWeight: 700 }}
          />
        )}
      </Box>

      {pageSummaries.length === 0 ? (
        <Alert severity="info">
          Website activity was matched, but no individual page URLs were recorded.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {pageSummaries.map((page) => {
            const prettyDuration =
              page.totalDurationSeconds > 0
                ? formatDurationSeconds(page.totalDurationSeconds)
                : null;

            return (
              <Card
                key={page.url}
                elevation={0}
                sx={{
                  border: `1px solid ${BRAND_BLUE}22`,
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ py: 2 }}>
                  <Stack spacing={1}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PublicIcon sx={{ color: BRAND_BLUE }} />
                          <Link
                            href={page.url}
                            target="_blank"
                            rel="noreferrer"
                            underline="hover"
                            sx={{
                              color: BRAND_BLUE,
                              fontWeight: 700,
                              wordBreak: "break-all",
                            }}
                          >
                            {page.url}
                          </Link>
                          <LaunchIcon sx={{ color: `${BRAND_BLUE}99`, fontSize: 18 }} />
                        </Stack>
                      </Box>

                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Chip label={`${page.visits} visits`} size="small" />
                        {prettyDuration && (
                          <Chip
                            label={`Time on page ${prettyDuration}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
